import { useMemo } from "react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Box, Group, Text, Paper, Stack, SimpleGrid, Badge, Loader } from "@mantine/core";
import { useGetParentsUTMStatsQuery } from "../redux/features/parentApi";

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Composant de statistiques UTM pour le back-office
 * Affiche les conversions par source, medium et campagne
 * Les données sont récupérées depuis l'API backend
 */
const UTMStatsChart = ({ title = "Statistiques de provenance (UTM)", startDate, endDate, type }) => {
  // Récupérer les données depuis l'API
  const { data: apiResponse, isLoading, isError } = useGetParentsUTMStatsQuery({
    startDate,
    endDate,
    type
  });

  const statsData = apiResponse?.data;

  // Données pour le graphique par source
  const sourceChartData = useMemo(() => {
    if (!statsData?.by_source) return null;

    return {
      labels: statsData.by_source.map(s => s.utm_source),
      datasets: [
        {
          label: "Contacts",
          data: statsData.by_source.map(s => s.contact),
          backgroundColor: "rgba(255, 193, 7, 0.7)",
        },
        {
          label: "Prospects",
          data: statsData.by_source.map(s => s.prospect),
          backgroundColor: "rgba(33, 150, 243, 0.7)",
        },
        {
          label: "Clients",
          data: statsData.by_source.map(s => s.client),
          backgroundColor: "rgba(76, 175, 80, 0.7)",
        },
      ],
    };
  }, [statsData]);

  // Données pour le graphique circulaire des sources
  const sourcePieData = useMemo(() => {
    if (!statsData?.by_source) return null;

    return {
      labels: statsData.by_source.map(s => s.utm_source),
      datasets: [
        {
          data: statsData.by_source.map(s => s.total),
          backgroundColor: [
            "rgba(59, 130, 246, 0.7)",
            "rgba(16, 185, 129, 0.7)",
            "rgba(245, 158, 11, 0.7)",
            "rgba(239, 68, 68, 0.7)",
            "rgba(139, 92, 246, 0.7)",
            "rgba(236, 72, 153, 0.7)",
            "rgba(20, 184, 166, 0.7)",
          ],
        },
      ],
    };
  }, [statsData]);

  // Options des graphiques
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      },
    },
  };

  if (isLoading) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Group position="center" style={{ minHeight: 200 }}>
          <Loader size="lg" />
          <Text>Chargement des statistiques UTM...</Text>
        </Group>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Text align="center" color="red">
          Erreur lors du chargement des statistiques UTM
        </Text>
      </Paper>
    );
  }

  if (!statsData || !statsData.by_source || statsData.by_source.length === 0) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Text align="center" color="dimmed">
          Aucune donnée UTM disponible
        </Text>
      </Paper>
    );
  }

  const { summary, by_source, by_campaign } = statsData;

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack spacing="md">
        {/* En-tête */}
        <Group position="apart">
          <Text weight={600} size="lg">
            {title}
          </Text>
          <Badge size="lg" color="blue">
            {summary.total_users} utilisateurs
          </Badge>
        </Group>

        {/* Métriques clés */}
        <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]}>
          <Paper p="sm" withBorder>
            <Text size="xs" color="dimmed">Contacts</Text>
            <Text size="xl" weight={700} color="orange">{summary.total_contacts}</Text>
          </Paper>
          <Paper p="sm" withBorder>
            <Text size="xs" color="dimmed">Prospects</Text>
            <Text size="xl" weight={700} color="blue">{summary.total_prospects}</Text>
          </Paper>
          <Paper p="sm" withBorder>
            <Text size="xs" color="dimmed">Clients</Text>
            <Text size="xl" weight={700} color="green">{summary.total_clients}</Text>
          </Paper>
          <Paper p="sm" withBorder>
            <Text size="xs" color="dimmed">Taux de conversion</Text>
            <Text size="xl" weight={700} color="violet">{summary.global_conversion_rate}%</Text>
          </Paper>
        </SimpleGrid>

        {/* Graphiques */}
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
          {/* Graphique en barres par source */}
          <Box>
            <Text weight={500} size="sm" mb="xs">
              Conversions par source
            </Text>
            <Box style={{ height: 300 }}>
              <Bar data={sourceChartData} options={barOptions} />
            </Box>
          </Box>

          {/* Graphique circulaire des sources */}
          <Box>
            <Text weight={500} size="sm" mb="xs">
              Répartition des sources
            </Text>
            <Box style={{ height: 300 }}>
              <Pie data={sourcePieData} options={pieOptions} />
            </Box>
          </Box>
        </SimpleGrid>

        {/* Tableau détaillé par source */}
        <Box>
          <Text weight={500} size="sm" mb="xs">
            Détails par source
          </Text>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e9ecef" }}>
                <th style={{ textAlign: "left", padding: "8px" }}>Source</th>
                <th style={{ textAlign: "center", padding: "8px" }}>Total</th>
                <th style={{ textAlign: "center", padding: "8px" }}>Contacts</th>
                <th style={{ textAlign: "center", padding: "8px" }}>Prospects</th>
                <th style={{ textAlign: "center", padding: "8px" }}>Clients</th>
                <th style={{ textAlign: "center", padding: "8px" }}>Taux</th>
              </tr>
            </thead>
            <tbody>
              {by_source.map((source) => (
                <tr key={source.utm_source} style={{ borderBottom: "1px solid #f1f3f5" }}>
                  <td style={{ padding: "8px" }}>
                    <Text weight={500}>{source.utm_source}</Text>
                  </td>
                  <td style={{ textAlign: "center", padding: "8px" }}>{source.total}</td>
                  <td style={{ textAlign: "center", padding: "8px" }}>
                    <Badge color="orange" variant="light">{source.contact}</Badge>
                  </td>
                  <td style={{ textAlign: "center", padding: "8px" }}>
                    <Badge color="blue" variant="light">{source.prospect}</Badge>
                  </td>
                  <td style={{ textAlign: "center", padding: "8px" }}>
                    <Badge color="green" variant="light">{source.client}</Badge>
                  </td>
                  <td style={{ textAlign: "center", padding: "8px" }}>
                    <Text size="sm" weight={500}>{source.conversion_rate}%</Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        {/* Tableau détaillé par campagne */}
        {by_campaign && by_campaign.length > 0 && (
          <Box>
            <Text weight={500} size="sm" mb="xs">
              Détails par campagne
            </Text>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e9ecef" }}>
                  <th style={{ textAlign: "left", padding: "8px" }}>Campagne</th>
                  <th style={{ textAlign: "center", padding: "8px" }}>Total</th>
                  <th style={{ textAlign: "center", padding: "8px" }}>Contacts</th>
                  <th style={{ textAlign: "center", padding: "8px" }}>Prospects</th>
                  <th style={{ textAlign: "center", padding: "8px" }}>Clients</th>
                  <th style={{ textAlign: "center", padding: "8px" }}>Taux</th>
                </tr>
              </thead>
              <tbody>
                {by_campaign.map((campaign) => (
                  <tr key={campaign.utm_campaign} style={{ borderBottom: "1px solid #f1f3f5" }}>
                    <td style={{ padding: "8px" }}>
                      <Text weight={500}>{campaign.utm_campaign}</Text>
                    </td>
                    <td style={{ textAlign: "center", padding: "8px" }}>{campaign.total}</td>
                    <td style={{ textAlign: "center", padding: "8px" }}>
                      <Badge color="orange" variant="light">{campaign.contact}</Badge>
                    </td>
                    <td style={{ textAlign: "center", padding: "8px" }}>
                      <Badge color="blue" variant="light">{campaign.prospect}</Badge>
                    </td>
                    <td style={{ textAlign: "center", padding: "8px" }}>
                      <Badge color="green" variant="light">{campaign.client}</Badge>
                    </td>
                    <td style={{ textAlign: "center", padding: "8px" }}>
                      <Text size="sm" weight={500}>{campaign.conversion_rate}%</Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default UTMStatsChart;
